/**
 * Insights Studio — AI-driven conversational analytics (INSIGHTS › Insights Studio).
 *
 * A manager asks questions in plain English; the AI plans queries against a SAFE
 * whitelisted analytics catalog (below), the backend compiles them to typed Prisma
 * reads (never raw SQL for members), and the answer comes back as narrative text plus
 * interactive chart/table cards with drill-down to the underlying time entries.
 *
 * Access is granted per role via the `insights-studio` menu item (area-less menu grant;
 * the backend explicitly checks the menu permission on every endpoint because Insights
 * Studio reads time data across ALL members — more than a technician normally sees).
 *
 * Phase 1 covers time entries only. The catalog is designed to gain more datasets later
 * (mirroring the dashboards `DASHBOARD_ENTITY_DEFS` extension recipe).
 */
// ---------------------------------------------------------------------------
// Analytics catalog (time entries)
// ---------------------------------------------------------------------------
/** Grouping dimensions the analytics engine understands. */
export const INSIGHTS_DIMENSIONS = [
    'member',
    'customer',
    'project',
    'agreement',
    'ticket',
    'serviceCategory',
    'billingClass',
    'billable',
    'entryMethod',
    'day',
    'week',
    'month',
    'quarter',
    'year',
];
export const INSIGHTS_DIMENSION_LABELS = {
    member: 'Member',
    customer: 'Customer',
    project: 'Project',
    agreement: 'Agreement',
    ticket: 'Ticket',
    serviceCategory: 'Service category',
    billingClass: 'Billing class',
    billable: 'Billable',
    entryMethod: 'Entered via',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    quarter: 'Quarter',
    year: 'Year',
};
/** Date-bucket dimensions (sorted chronologically instead of by measure). */
export const INSIGHTS_DATE_DIMENSIONS = [
    'day',
    'week',
    'month',
    'quarter',
    'year',
];
/** Numeric measures the engine can aggregate. Revenue = hours × resolved rate. */
export const INSIGHTS_MEASURES = [
    'hours',
    'billableHours',
    'nonBillableHours',
    'revenue',
    'entries',
    'avgRate',
];
export const INSIGHTS_MEASURE_LABELS = {
    hours: 'Hours',
    billableHours: 'Billable hours',
    nonBillableHours: 'Non-billable hours',
    revenue: 'Revenue',
    entries: 'Entries',
    avgRate: 'Avg rate',
};
/** Max keyword search terms per query. */
export const INSIGHTS_MAX_KEYWORDS = 10;
// ---------------------------------------------------------------------------
// Domains (widened catalog)
//
// Insights Studio answers from FOUR whitelisted domains, all sharing the same
// query-spec shape. Every dimension/measure is validated against its domain's
// catalog server-side; anything outside it is a hard, explicit error so the AI
// says "I cannot see X yet" instead of quietly answering with hours.
// Cross-domain relationships exist ONLY where a measure declares them (e.g. the
// money domain's burn measures read time entries against the agreement cap).
// ---------------------------------------------------------------------------
export const INSIGHTS_DOMAINS = ['time', 'tickets', 'money', 'pipeline'];
export const INSIGHTS_DOMAIN_CATALOG = {
    time: {
        label: 'Time',
        description: 'Time entries: hours, billable mix, revenue (hours x resolved rate).',
        dateAnchor: 'work date',
        dimensions: [
            { key: 'member', label: 'Member' },
            { key: 'customer', label: 'Customer' },
            { key: 'project', label: 'Project' },
            { key: 'agreement', label: 'Agreement' },
            { key: 'ticket', label: 'Ticket' },
            { key: 'serviceCategory', label: 'Service category' },
            { key: 'billingClass', label: 'Billing class' },
            { key: 'billable', label: 'Billable' },
            { key: 'entryMethod', label: 'Entry method' },
            { key: 'day', label: 'Day' },
            { key: 'week', label: 'Week' },
            { key: 'month', label: 'Month' },
            { key: 'quarter', label: 'Quarter' },
            { key: 'year', label: 'Year' },
        ],
        measures: [
            { key: 'hours', label: 'Hours', format: 'hours' },
            { key: 'billableHours', label: 'Billable hours', format: 'hours' },
            { key: 'nonBillableHours', label: 'Non-billable hours', format: 'hours' },
            { key: 'revenue', label: 'Revenue', format: 'currency' },
            { key: 'entries', label: 'Entries', format: 'count' },
            { key: 'avgRate', label: 'Avg rate', format: 'currency' },
        ],
        filters: [
            'period',
            'dateFrom',
            'dateTo',
            'memberIds',
            'customerIds',
            'projectIds',
            'agreementIds',
            'ticketIds',
            'serviceCategoryIds',
            'billingClassIds',
            'billable',
            'writtenOff',
            'keywords',
        ],
    },
    tickets: {
        label: 'Tickets',
        description: 'Tickets: volumes, status/type/priority mix, open age, time-to-close, time to first logged work.',
        dateAnchor: 'ticket created date',
        dimensions: [
            { key: 'status', label: 'Status' },
            { key: 'type', label: 'Type' },
            { key: 'priority', label: 'Priority' },
            { key: 'origin', label: 'Origin' },
            { key: 'assignee', label: 'Assignee' },
            { key: 'customer', label: 'Customer' },
            { key: 'ageBucket', label: 'Age bucket' },
            { key: 'month', label: 'Created month' },
        ],
        measures: [
            { key: 'count', label: 'Tickets', format: 'count' },
            { key: 'openCount', label: 'Open tickets', format: 'count' },
            { key: 'closedCount', label: 'Closed tickets', format: 'count' },
            { key: 'medianAgeDays', label: 'Median open age (days)', format: 'days' },
            { key: 'medianTimeToCloseDays', label: 'Median time to close (days)', format: 'days' },
            { key: 'medianFirstWorkDays', label: 'Median days to first work', format: 'days' },
        ],
        filters: [
            'period',
            'dateFrom',
            'dateTo',
            'memberIds',
            'customerIds',
            'projectIds',
            'agreementIds',
            'ticketIds',
            'keywords',
        ],
    },
    money: {
        label: 'Money',
        description: 'Billing: invoiced revenue, unbilled/WIP value of hours, realization rate, and managed-services burn against the included-hours cap (current month).',
        dateAnchor: 'invoice issue date for invoice measures, work date for hour measures',
        dimensions: [
            { key: 'customer', label: 'Customer' },
            { key: 'agreement', label: 'Agreement' },
            { key: 'agreementType', label: 'Agreement type' },
            { key: 'serviceCategory', label: 'Service category' },
            { key: 'month', label: 'Month' },
        ],
        measures: [
            { key: 'billedAmount', label: 'Billed', format: 'currency' },
            { key: 'invoiceCount', label: 'Invoices', format: 'count' },
            { key: 'unbilledHours', label: 'Unbilled hours', format: 'hours' },
            { key: 'unbilledAmount', label: 'Unbilled value', format: 'currency' },
            { key: 'wipAmount', label: 'WIP (approved unbilled)', format: 'currency' },
            { key: 'realizationRate', label: 'Realization rate', format: 'percent' },
            { key: 'hoursConsumed', label: 'MS hours consumed (this month)', format: 'hours' },
            { key: 'hoursIncluded', label: 'MS hours included', format: 'hours' },
            { key: 'burnRate', label: 'MS burn vs cap', format: 'percent' },
        ],
        filters: [
            'period',
            'dateFrom',
            'dateTo',
            'customerIds',
            'agreementIds',
            'projectIds',
            'serviceCategoryIds',
        ],
    },
    pipeline: {
        label: 'Pipeline',
        description: 'Sales pipeline: opportunity counts and value by stage/owner/customer/type, win rate, open age, latest quotes.',
        dateAnchor: 'opportunity close date',
        dimensions: [
            { key: 'stage', label: 'Stage' },
            { key: 'status', label: 'Status' },
            { key: 'owner', label: 'Sales rep' },
            { key: 'customer', label: 'Customer' },
            { key: 'type', label: 'Type' },
            { key: 'closeMonth', label: 'Close month' },
        ],
        measures: [
            { key: 'count', label: 'Opportunities', format: 'count' },
            { key: 'value', label: 'Value', format: 'currency' },
            { key: 'wonValue', label: 'Won value', format: 'currency' },
            { key: 'winRate', label: 'Win rate', format: 'percent' },
            { key: 'avgAgeDays', label: 'Avg open age (days)', format: 'days' },
            { key: 'quoteCount', label: 'Quotes', format: 'count' },
        ],
        filters: ['period', 'dateFrom', 'dateTo', 'memberIds', 'customerIds', 'serviceCategoryIds'],
    },
};
/** The catalog for a spec's domain (missing domain = 'time', the pre-domain default). */
export function insightsDomainCatalog(domain) {
    return INSIGHTS_DOMAIN_CATALOG[domain ?? 'time'];
}
export function insightsDimensionLabel(domain, key) {
    return insightsDomainCatalog(domain).dimensions.find((d) => d.key === key)?.label ?? key;
}
export function insightsMeasureLabel(domain, key) {
    return insightsDomainCatalog(domain).measures.find((m) => m.key === key)?.label ?? key;
}
export function insightsMeasureFormat(domain, key) {
    return insightsDomainCatalog(domain).measures.find((m) => m.key === key)?.format ?? 'count';
}
/**
 * Relative date-period tokens resolved SERVER-SIDE (in the billing timezone) so the
 * model never has to compute calendar dates itself. The resolved concrete range is
 * echoed back in the tool result.
 */
export const INSIGHTS_PERIODS = ['mtd', 'qtd', 'ytd', 'last_quarter', 'trailing_90'];
/** Ceiling on grouped rows returned by one query. */
export const INSIGHTS_MAX_GROUPS = 100;
/** Default top-N group limit when the spec omits one. */
export const INSIGHTS_DEFAULT_GROUP_LIMIT = 25;
/** Chart types Insights Studio renders (dependency-free inline SVG). */
export const INSIGHTS_CHART_TYPES = ['bar', 'stacked_bar', 'line', 'pie', 'table'];
// ---------------------------------------------------------------------------
// Chat + conversations
// ---------------------------------------------------------------------------
/** Max stored conversation title length (auto-generated or renamed). */
export const INSIGHTS_CONVERSATION_TITLE_MAX = 60;
// ---------------------------------------------------------------------------
// Drill-down
// ---------------------------------------------------------------------------
/** Max entries returned by one drill-down request. */
export const INSIGHTS_DRILL_LIMIT = 200;
// ---------------------------------------------------------------------------
// Saved questions (lightweight report builder — the QUESTION is saved, not the result)
// ---------------------------------------------------------------------------
export const INSIGHTS_SAVED_QUESTION_MAX = 500;
//# sourceMappingURL=insights-studio.js.map